import { useEffect, useRef, useState } from 'react';
import { X, ExternalLink, Video, Maximize2 } from 'lucide-react';
import { isNativeApp, openExternal } from '@/lib/native';

/**
 * Shows a live class inside the app. Jitsi rooms and YouTube streams load in a frame; Google Meet, Zoom
 * and Teams refuse to be shown inside other websites, so they open in a separate window beside the app.
 */
export function LiveRoom({ title, embedUrl, joinUrl, provider, onClose }: { title: string; embedUrl: string | null; joinUrl: string; provider: string; onClose: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  const [popup, setPopup] = useState<Window | null>(null);
  const label = ({ meet: 'Google Meet', zoom: 'Zoom', teams: 'Microsoft Teams' } as Record<string, string>)[provider] ?? 'the meeting';

  const openWindow = () => {
    if (isNativeApp) { void openExternal(joinUrl); return; }
    const w = Math.min(1280, window.screen.availWidth - 80);
    const h = Math.min(820, window.screen.availHeight - 80);
    const win = window.open(joinUrl, 'ojp-live', `popup=yes,width=${w},height=${h},left=40,top=40`);
    setPopup(win);
    if (!win) window.open(joinUrl, '_blank', 'noopener');
  };
  useEffect(() => { if (!embedUrl) openWindow(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="fixed inset-0 z-[60] bg-slate-950 flex flex-col">
      <div className="flex items-center gap-2 px-3 py-2 text-white bg-slate-900">
        <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" /><p className="font-semibold text-sm truncate flex-1">{title}</p>
        {embedUrl && <button onClick={() => box.current?.requestFullscreen?.()} className="p-2 rounded-lg hover:bg-white/10" title="Full screen"><Maximize2 className="w-4 h-4" /></button>}
        <a href={joinUrl} target="_blank" rel="noreferrer" className="p-2 rounded-lg hover:bg-white/10" title="Open in a new tab"><ExternalLink className="w-4 h-4" /></a>
        <button onClick={() => { popup?.close(); onClose(); }} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-sm"><X className="w-4 h-4" />Leave</button>
      </div>
      {embedUrl ? (
        <div ref={box} className="flex-1 bg-black"><iframe title={title} src={embedUrl} className="w-full h-full border-0" allow="camera; microphone; fullscreen; display-capture; autoplay; clipboard-write" allowFullScreen /></div>
      ) : (
        <div className="flex-1 grid place-items-center p-6 text-center text-white">
          <div className="max-w-md">
            <span className="w-16 h-16 rounded-2xl bg-white/10 grid place-items-center mx-auto"><Video className="w-8 h-8" /></span>
            <h2 className="text-xl font-bold mt-4">The class is open in {label}</h2>
            <p className="text-sm text-white/70 mt-2">{label} doesn't allow other websites to show its calls inside them, so it opened in its own window next to this one. Your attendance has been recorded.</p>
            <button onClick={openWindow} className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-green-600 text-sm font-semibold"><ExternalLink className="w-4 h-4" />Open {label} again</button>
            <p className="text-xs text-white/50 mt-3">Tip: for calls that stay inside the app, the teacher can choose “Create a room for me”.</p>
          </div>
        </div>
      )}
    </div>
  );
}
